import { quantidadeComUnidade } from "@plantus/ui";
import type { Lote } from "@plantus/api-client";

/**
 * *Quanto, até quando, de onde* — o bloco de dados do lote.
 *
 * <p>Saiu de dentro de {@code PaginaLote} quando o lote ganhou abas (#36):
 * **duas telas passaram a responder a mesma pergunta**, a ficha que o QR abre e
 * a aba *Resumo*. Duplicar o `<dl>` seria duplicar seis decisões de leitura — a
 * quantidade com vírgula, a palavra *média* escrita ao lado do número, o
 * `VENCIDO` em texto ao lado da cor, a procedência que não vira travessão — e a
 * cópia que ninguém está olhando é sempre a que fica para trás.
 *
 * <p>É o mesmo argumento de {@code situacaoDoLote.ts}: mapa duplicado é mapa que
 * diverge.
 */
export function DadosDoLote({ lote }: { lote: Lote }) {
  return (
    <dl className="lote__dados">
      <div>
        <dt>Quantidade</dt>
        {/* `quantidadeComUnidade`, nunca o texto cru do servidor:
            `120.000000 kg` se lê como CENTO E VINTE MIL em português,
            e esta é uma tela consultada com o material na frente. */}
        <dd className="lote__valor">
          {quantidadeComUnidade(lote.quantidade, lote.unidade)}
        </dd>
      </div>

      <div>
        <dt>Volumes</dt>
        {/* Aqui a pergunta é sobre o LOTE, e a resposta é média — com a
            palavra escrita. O peso do recipiente que está na mão fica no
            bloco lá em cima, e é o gravado.

            🔴 Enquanto a balança da conferência não existir, os dois
            números coincidem: o peso gravado do volume nasce desta mesma
            divisão. Ver docs/backend/producao.md. */}
        <dd className="lote__valor">
          {lote.volumes}
          <span className="lote__sub">
            média de {quantidadeComUnidade(lote.quantidadePorVolume, lote.unidade)} por
            volume
          </span>
        </dd>
      </div>

      <div>
        <dt>Validade</dt>
        <dd className={lote.vencido ? "lote__vencido" : "lote__valor"}>
          {formatar(lote.validade)}
          {lote.vencido && " · VENCIDO"}
        </dd>
      </div>

      <div>
        <dt>Recebido em</dt>
        <dd className="lote__valor">{formatar(lote.recebidoEm)}</dd>
      </div>

      <div>
        <dt>Procedência</dt>
        {/* "Fazenda própria" vem resolvido do servidor quando a origem é
            do grupo — em vez de um travessão, que se lê como dado
            faltando. */}
        <dd className="lote__valor">
          {lote.fornecedor}
          {lote.loteDoFornecedor !== null && lote.loteDoFornecedor !== "" && (
            <span className="lote__sub">lote do fornecedor {lote.loteDoFornecedor}</span>
          )}
        </dd>
      </div>

      {lote.notaFiscal !== null && lote.notaFiscal !== "" && (
        <div>
          <dt>Nota fiscal</dt>
          <dd className="lote__valor">{lote.notaFiscal}</dd>
        </div>
      )}
    </dl>
  );
}

/** Data do calendário, formatada sem passar por fuso. Ver Etiqueta.tsx. */
function formatar(iso: string): string {
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
}
